/**
 * Tab Navigator (chat-centric revamp §6.7): Chat · Book · Feed · Settings.
 *
 * Settings is a real fourth tab (2026-11-01 spec). It used to be a bespoke 👤
 * emoji column in a custom trailing slot beside the library bar, which read
 * bigger, brighter and unlabelled next to Chat · Book · Feed because nothing
 * tied it to the bar's icon/label/tint grammar. Registering the stack instead
 * makes the consistency structural: icon, label and focus tint come from
 * createBottomTabNavigator.
 *
 * The Dashboard/Portfolio/Notifications navigators are not tabs; they stay
 * registered in AuthNavigator so screens that still navigate into them keep
 * working until the T8/T9 sweep re-homes those flows. Settings is reachable
 * only as this bar tab — its root-stack registration was removed in the same
 * change.
 */

import { StyleSheet, View } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { NavigatorScreenParams } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

import { useNotificationCounts } from '../services/queries/useNotificationCounts';
import { ChatNavigatorParamList, ChatNavigator } from './ChatNavigator';
import { SettingsNavigatorParamList, SettingsNavigator } from './SettingsNavigator';
import { BookScreen } from '../screens/book/BookScreen';
import { FeedScreen } from '../screens/feed/FeedScreen';
import { DocumentImportBanner } from '../components/ui/DocumentImportBanner';
import { DocumentImportLoadingBar } from '../components/ui/DocumentImportLoadingBar';

export type TabNavigatorParamList = {
  Chat: NavigatorScreenParams<ChatNavigatorParamList>;
  Book: undefined;
  Feed: undefined;
  Settings: NavigatorScreenParams<SettingsNavigatorParamList>;
};

const Tab = createBottomTabNavigator<TabNavigatorParamList>();

export function TabNavigator() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  // On counts-fetch failure the badge stays hidden (total ?? 0); React Query
  // retries per its defaults and the mutations re-invalidate on success.
  const { data: counts } = useNotificationCounts();
  const badge = counts?.total ?? 0;

  return (
    <View style={{ flex: 1 }}>
      <DocumentImportBanner />
      <DocumentImportLoadingBar />
      <Tab.Navigator
        screenOptions={({ route }) => ({
          headerShown: false,
          tabBarIcon: ({ focused, color, size }) => {
            let iconName: keyof typeof Ionicons.glyphMap = 'help-circle';
            if (route.name === 'Book') {
              iconName = focused ? 'book' : 'book-outline';
            } else if (route.name === 'Feed') {
              iconName = focused ? 'newspaper' : 'newspaper-outline';
            } else if (route.name === 'Chat') {
              iconName = focused ? 'chatbubbles' : 'chatbubbles-outline';
            } else if (route.name === 'Settings') {
              iconName = focused ? 'settings' : 'settings-outline';
            }
            return <Ionicons name={iconName} size={size} color={color} />;
          },
          tabBarActiveTintColor: theme.colors.primary,
          tabBarInactiveTintColor: theme.colors.textTertiary,
          // The library bar draws its own chrome now that the wrapper row is
          // gone: hairline top border over the elevated surface, both themed.
          tabBarStyle: {
            backgroundColor: theme.colors.backgroundElevated,
            borderTopColor: theme.colors.border,
            borderTopWidth: StyleSheet.hairlineWidth,
          },
        })}
      >
        <Tab.Screen
          name="Chat"
          component={ChatNavigator}
          options={{ tabBarLabel: t('tabs.chat', { defaultValue: 'Ask TopNote' }) }}
        />
        <Tab.Screen
          name="Book"
          component={BookScreen}
          options={{ tabBarLabel: t('tabs.book', { defaultValue: 'Book' }) }}
        />
        <Tab.Screen
          name="Feed"
          component={FeedScreen}
          options={{
            tabBarLabel: t('tabs.feed', { defaultValue: 'Feed' }),
            // The Feed tab inherits the Notifications badge wiring: the
            // combined unread counts, read-aware since Task 3.
            tabBarBadge: badge > 99 ? '99+' : badge > 0 ? badge : undefined,
          }}
        />
        <Tab.Screen
          name="Settings"
          component={SettingsNavigator}
          options={{ tabBarLabel: t('tabs.settings', { defaultValue: 'Settings' }) }}
        />
      </Tab.Navigator>
    </View>
  );
}
